import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Play, Search, Calendar, Clock, X } from 'lucide-react';
import { episodes } from '../data/mock';

// Component for episode thumbnail with fallback
const EpisodeThumbnail = ({ episode }) => {
  const [imgSrc, setImgSrc] = useState(episode.thumbnail);
  
  const handleImageError = () => {
    // If maxresdefault fails, try hqdefault
    if (imgSrc.includes('maxresdefault')) {
      setImgSrc(imgSrc.replace('maxresdefault', 'hqdefault'));
    }
  };

  return (
    <div
      className="h-48 bg-cover bg-center relative overflow-hidden bg-dark-card"
      style={{ backgroundImage: `url(${imgSrc})` }}
    >
      {/* Hidden img to detect load errors */}
      <img 
        src={imgSrc} 
        alt="" 
        className="hidden" 
        onError={handleImageError}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-dark-bg to-transparent"></div>
      {episode.isShort && (
        <div className="absolute top-4 left-4">
          <span
            className="text-[10px] font-semibold uppercase tracking-widest bg-gold text-dark-bg px-2.5 py-1"
            data-testid="episode-short-badge"
          >
            Short
          </span>
        </div>
      )}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="w-14 h-14 bg-gold flex items-center justify-center">
          <Play
            className="w-6 h-6 text-dark-bg ml-1"
            fill="currentColor"
          />
        </div>
      </div>
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-warm-white/70 text-xs uppercase tracking-wider">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {new Date(episode.date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {episode.duration}
        </span>
      </div>
    </div>
  );
};

const Episodes = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  // Get all unique topics
  const allTopics = useMemo(() => {
    const topics = new Set();
    episodes.forEach((episode) => {
      episode.topics.forEach((topic) => topics.add(topic));
    });
    return Array.from(topics).sort();
  }, []);

  // Filter episodes
  const filteredEpisodes = useMemo(() => {
    return episodes.filter((episode) => {
      const matchesSearch =
        episode.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        episode.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTopic =
        !selectedTopic || episode.topics.includes(selectedTopic);
      return matchesSearch && matchesTopic;
    });
  }, [searchQuery, selectedTopic]);

  return (
    <div className="min-h-screen pt-24 pb-20 bg-dark-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 pt-8">
          <p className="text-gold text-xs font-medium uppercase tracking-widest mb-4">
            Archive
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-warm-white mb-4 tracking-tight">
            All Episodes
          </h1>
          <p className="text-warm-white/60 max-w-2xl">
            Browse our complete collection of episodes. Search by title or
            filter by topic to find exactly what you&apos;re looking for.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="bg-dark-card border border-dark-border p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-white/40" />
              <Input
                type="text"
                placeholder="Search episodes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 h-12 bg-dark-bg border-dark-border text-warm-white placeholder:text-warm-white/40 focus:border-gold focus:ring-gold"
              />
            </div>
          </div>

          {/* Topic Filters */}
          <div className="mt-6 flex flex-wrap gap-2">
            <Button
              variant={selectedTopic === '' ? 'default' : 'outline'}
              onClick={() => setSelectedTopic('')}
              className={
                selectedTopic === ''
                  ? 'bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-xs uppercase tracking-wider'
                  : 'border-dark-border text-warm-white/60 hover:border-gold hover:text-gold bg-transparent text-xs uppercase tracking-wider'
              }
            >
              All Topics
            </Button>
            {allTopics.map((topic) => (
              <Button
                key={topic}
                variant={selectedTopic === topic ? 'default' : 'outline'}
                onClick={() =>
                  setSelectedTopic(selectedTopic === topic ? '' : topic)
                }
                className={
                  selectedTopic === topic
                    ? 'bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-xs uppercase tracking-wider'
                    : 'border-dark-border text-warm-white/60 hover:border-gold hover:text-gold bg-transparent text-xs uppercase tracking-wider'
                }
              >
                {topic}
                {selectedTopic === topic && (
                  <X className="ml-2 w-3 h-3" />
                )}
              </Button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-8">
          <p className="text-warm-white/50 text-xs uppercase tracking-widest">
            Showing <span className="text-gold">{filteredEpisodes.length}</span>{' '}
            {filteredEpisodes.length === 1 ? 'episode' : 'episodes'}
          </p>
        </div>

        {/* Episodes Grid */}
        {filteredEpisodes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEpisodes.map((episode) => (
              <a
                key={episode.id}
                href={episode.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Card
                  className="overflow-hidden hover:border-gold/50 transition-all duration-300 group border border-dark-border bg-dark-card cursor-pointer h-full"
                >
                  <EpisodeThumbnail episode={episode} />
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-warm-white mb-2 group-hover:text-gold transition-colors leading-tight">
                      {episode.title}
                    </h3>
                    <p className="text-warm-white/60 mb-4 line-clamp-3 text-sm leading-relaxed">
                      {episode.description}
                    </p>
                    <div className="flex flex-wrap gap-3 mb-4">
                      {episode.topics.map((topic) => (
                        <span
                          key={topic}
                          className="text-xs text-gold/70 uppercase tracking-wider"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                    <div className="w-full bg-gold hover:bg-gold/90 text-dark-bg font-semibold py-3 px-4 text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2">
                      <Play className="w-3 h-3" fill="currentColor" />
                      Play Episode
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-dark-card border border-dark-border flex items-center justify-center mx-auto mb-6">
              <Search className="w-8 h-8 text-warm-white/40" />
            </div>
            <h3 className="text-xl font-semibold text-warm-white mb-2">
              No episodes found
            </h3>
            <p className="text-warm-white/60 mb-8">
              Try adjusting your search or filter criteria
            </p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setSelectedTopic('');
              }}
              className="bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-xs uppercase tracking-wider"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Episodes;
